import { Component, Input, OnInit } from '@angular/core';

import {
  DashboardEngineiusKPIComponent,
  MetricDisplayStyle,
} from './dashboard-engineius-kpi.component';

enum ChartMetric {
  CompletedMoves,
  NumberOfActiveSupplierDrivers,
  NumberOfActiveSupplierAgents,
  JobsPerActiveDriverPerMonth,
  JobsPerActiveAgentPerMonth,
  CallsPerCompletedJob,
  PerfectMovesPercent,
  DriversFullyOperational,
  AgentsFullyOperational,
  DriverSuppliersDeactivated,
  AgentSuppliersDeactivated,
  RetentionDrivers,
  RetentionAgents,
  SystemUptime,
  MovementsWithFullDataCapture,
}

@Component({
  selector: 'dashboard-engineius-operations-kpi',
  styleUrls: ['./dashboard-engineius-kpi.component.scss'],
  templateUrl: './dashboard-engineius-kpi.component.html',
})
export class DashboardEngineiusKPIOperationsComponent extends DashboardEngineiusKPIComponent {
  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    //console.log("Operational INIT");
    this.selectedMonthRange = 12;

    // load chart data
    await this.loadData();
  }

  async loadData() {
    //console.log("Loading Commercial chart data");

    await super.loadData();

    this.monthlyTotals = await this.kpiService.getKPIOperationalTotals(
      this.kpiStartDate,
      this.selectedMonthRange,
    );

    this.setMonthDates(this.selectedMonthRange);

    this.completedTotals = this.monthlyTotals.filter((x) => !x.isCancelled);
    //console.log("Operational totals: ", this.completedTotals);

    this.totalsChartData = [];

    const dataMap = new Map<number, number[]>([
      [
        ChartMetric.CompletedMoves,
        this.completedTotals.map((x) => {
          return x.totalCompleted;
        }),
      ],
      [
        ChartMetric.NumberOfActiveSupplierDrivers,
        this.completedTotals.map((x) => {
          return x.numberOfActiveSupplierDrivers;
        }),
      ],
      [
        ChartMetric.NumberOfActiveSupplierAgents,
        this.completedTotals.map((x) => {
          return x.numberOfActiveSupplierAgents;
        }),
      ],
      [
        ChartMetric.JobsPerActiveDriverPerMonth,
        this.completedTotals.map((x) => {
          return x.jobsPerActiveDriverPerMonth;
        }),
      ],
      [
        ChartMetric.JobsPerActiveAgentPerMonth,
        this.completedTotals.map((x) => {
          return x.jobsPerActiveAgentPerMonth;
        }),
      ],
      [
        ChartMetric.CallsPerCompletedJob, // todo when metric available
        this.completedTotals.map((x) => {
          return x.totalCompleted;
        }),
      ],
      [
        ChartMetric.PerfectMovesPercent,
        this.completedTotals.map((x) => {
          return x.perfectMovesPercent;
        }),
      ],
      [
        ChartMetric.DriversFullyOperational,
        this.completedTotals.map((x) => {
          return x.driversFullyOperational;
        }),
      ],
      [
        ChartMetric.AgentsFullyOperational,
        this.completedTotals.map((x) => {
          return x.agentsFullyOperational;
        }),
      ],
      [
        ChartMetric.DriverSuppliersDeactivated,
        this.completedTotals.map((x) => {
          return x.driverSuppliersDeactivated;
        }),
      ],
      [
        ChartMetric.AgentSuppliersDeactivated,
        this.completedTotals.map((x) => {
          return x.agentSuppliersDeactivated;
        }),
      ],
      [
        ChartMetric.RetentionDrivers,
        this.completedTotals.map((x) => {
          return x.retentionDrivers;
        }),
      ],
      [
        ChartMetric.RetentionAgents,
        this.completedTotals.map((x) => {
          return x.retentionAgents;
        }),
      ],
    ]);

    this.totalsChartData = Array.from(dataMap.values());
    //console.log("Operational Totals chart data: ", this.totalsChartData);

    // create metric card data for each kpi measure
    this.monthlyMetrics = [];
    this.createChart_ActiveSuppliers();
    this.createChart_OperationalData();
    this.createChart_Retention();
    //this.createChart_PerfectMoves();

    this.isLoading = false;

    //console.log("Operational Metrics: ", this.monthlyMetrics);
  }

  createChart_ActiveSuppliers() {
    let metrics = [];

    //let metric = this.getMetricData_v2("Active Suppliers", ChartMetric.NumberOfActiveSuppliers, false, false, false);
    let metric = this.getNoDataMetric('Active Suppliers');
    let chartData = this.getTwoSetOneScaleChartData(
      ChartMetric.NumberOfActiveSupplierDrivers,
      ChartMetric.NumberOfActiveSupplierAgents,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Active Drivers',
      'Active Agents',
    );

    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip =
      'Number of active drivers / suppliers with a task completed within the last 65 days';

    //metric.chartData.options = this.baseChartOptions;
    metric.totalName = 'Active';
    metrics.push(metric);

    // next metric
    //metric = this.getMetricData_v2("Jobs Per Active Driver/Agent", ChartMetric.JobsPerActiveDriverPerMonth, false, false, false);
    metric = this.getNoDataMetric('Jobs Per Active Supplier');
    chartData = this.getTwoSetOneScaleChartData(
      ChartMetric.JobsPerActiveDriverPerMonth,
      ChartMetric.JobsPerActiveAgentPerMonth,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Jobs Per Driver',
      'Jobs Per Agent',
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip =
      'Average number of tasks per month for active suppliers ie, where at least one completed tasks within last 65 days';
    metric.totalName = 'Jobs';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Active Suppliers and Jobs', metrics });
  }

  createChart_OperationalData() {
    let metrics = [];

    //let metric = this.getMetricData_v2("Full Operational Suppliers", ChartMetric.DriversFullyOperational, false, false, false);
    //let chartData = this.getSingleChartData(ChartMetric.DriversFullyOperational, 'rgba(0, 128, 203, 0.7)', "Operational");
    let metric = this.getNoDataMetric('Fully Operational Suppliers');
    let chartData = this.getTwoSetOneScaleChartData(
      ChartMetric.DriversFullyOperational,
      ChartMetric.AgentsFullyOperational,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Operational Drivers',
      'Operational Agents',
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip = 'Number of live drivers who have completed 10 or more jobs';

    //metric.chartData.options = this.baseChartOptions;
    metric.totalName = 'Operational';
    metrics.push(metric);

    // next metric
    //metric = this.getMetricData_v2("Suppliers Deactivated", ChartMetric.SuppliersDeactivated, false, false, false);
    //chartData = this.getSingleChartData(ChartMetric.SuppliersDeactivated, 'rgba(245, 140, 98, 0.9)', "Deactivated");
    metric = this.getNoDataMetric('Deactivated Suppliers');
    chartData = this.getTwoSetOneScaleChartData(
      ChartMetric.DriverSuppliersDeactivated,
      ChartMetric.AgentSuppliersDeactivated,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Deactivated Drivers',
      'Deactivated Agents',
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip =
      'Count of deactivated suppliers, ie, suppliers where no task conducted within last 65 days';

    metric.totalName = 'Deactivated';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Operational Supplier Metrics', metrics });
  }

  createChart_PerfectMoves() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Perfect Moves',
      ChartMetric.PerfectMovesPercent,
      false,
      true,
      false,
      MetricDisplayStyle.Percentage,
    );
    let chartData = this.getPercentageChartData(
      ChartMetric.PerfectMovesPercent,
      'Perfect Moves',
      'rgba(43, 227, 104, 1)',
      'line',
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip =
      "Number of 'Perfect Jobs' as a percentage of total tasks. It is bought off the mobile portal. 'Perfect Job' = 'At system job price','Completed on time' and 'With no negative customer feedback'";

    metric.totalName = 'Perfect';
    metric.useValueAsPercent = true;
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Perfect Moves', metrics });
  }

  createChart_Retention() {
    let metrics = [];

    // next metric
    let metric = this.getNoDataMetric('Supplier Retention'); // this.getMetricData_v2("Supplier Retention", ChartMetric.Retention, false, true, false);
    let chartData2 = this.getTwoSetChartData(
      ChartMetric.RetentionDrivers,
      ChartMetric.RetentionAgents,
      'rgba(20, 120, 55, 0.7)',
      'rgba(15, 54, 125, 0.7)',
      'Driver Retention',
      'Agent Retention',
      null,
      null,
      'line',
      'line',
      this.twoSetChartPercentageOptions,
    );
    metric.chartData = chartData2;
    metric.noComp = true;
    metric.tooltip =
      'Count of all drivers minus total deactivate drivers in the month)/ total number of activate suppliers';

    metric.totalName = 'Retention';
    metric.useValueAsPercent = true;
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Retention', metrics });
  }
}
